import React, {useCallback, useEffect} from 'react';

import Canvas from 'components/Canvas'
import useDomino from 'hooks/useDomino';
import useSession from 'hooks/useSession';
import useColor from 'hooks/useColor';
import useGroup from 'hooks/useGroup';
import useSetting from 'hooks/useSetting';
import Html from 'helpers/Html';

let context = false;
let canvas = false;
let isMovePaint = false;
let timer = false;
let dominoPaintWithMove = [];
let blockNextDraw = false;

function Workspace(props) {
	const {
		currentZoom,
		setFetchingGroup,
		canvasMap,
		wrapper
	} = props;

	const {
		setup,
		planner
	} = useSession();

	const {
		color
	} = useColor();

	const {
		fetching
	} = useGroup();

	const {
		saveMultipleDomino,
		saveSelection,
		dominoes,
		getLineAndColumn,
		config
	} = useDomino();

	const {
		tools,
		TOOLS_KEY,
		MODE_KEY,
		selection,
		mode,
		changeTools
	} = useSetting();

	const {
		setCurrent
	} = useColor();

	const {
		updateZoom,
		setMoveGroup,
		disabledMoveGroup,
		moveGroup,
		group
	} = useGroup();


	const copyCanvasOnMap = () => {
		let canvasMiniMap = canvasMap.current;
		let contextMiniMap = canvasMiniMap.getContext('2d');
		canvasMiniMap.width = canvas.width;
		canvasMiniMap.height = canvas.height;
		contextMiniMap.drawImage(canvas, 0, 0);
	}

	const drawDomino = useCallback((ctx, data, init = false) => {
		let {
			column,
			line,
			col = false
		} = data;

		let x = column * (config.DOMINO_WIDTH + config.MARGIN_WIDTH);
		let y = line * (config.DOMINO_HEIGHT + config.MARGIN_HEIGHT);
		let width = config.DOMINO_WIDTH;
		let height = config.DOMINO_HEIGHT;

		if (init === false && tools === TOOLS_KEY.rubber) {
			ctx.clearRect(x, y, width, height);
			return;
		}

		if (!col && !color) {
			return;
		}

		let colorHex = col ? col : color.color;

		if (colorHex === 'transparent') {
			var grd = ctx.createLinearGradient(x, y, x + width, y + height);
			grd.addColorStop(0, '#CECECE');
			grd.addColorStop(0.55, '#1ea8e7');
			grd.addColorStop(1, '#CECECE');
			colorHex = grd;
		}
		ctx.beginPath();
		ctx.fillStyle = colorHex;
		ctx.rect(
			x,
			y,
			width,
			height
		);
		ctx.closePath();
		ctx.fill();
	}, [color, tools, setup]);

	const drawGrid = (ctx, clear = true) => {
		if (clear) {
			ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
			ctx.restore();
		}
		let maxLineX = planner.width / config.DOMINO_WIDTH;
		for (var x = 0; x <= maxLineX; x += 1) {
			let moveX = (x * config.DOMINO_WIDTH) + ((x - 1) * config.MARGIN_WIDTH);
			ctx.rect(moveX, 0, config.MARGIN_WIDTH, planner.height);
		}

		let maxLineY = planner.height / config.DOMINO_HEIGHT;
		for (var y = 0; y <= maxLineY; y += 1) {
			let moveY = (y * config.DOMINO_HEIGHT) + ((y - 1) * config.MARGIN_HEIGHT);
			ctx.rect(0, moveY, planner.width, config.MARGIN_HEIGHT);
		}
		//ctx.strokeStyle = "black";
		//ctx.stroke();
		ctx.fillStyle = setup.backgroundColor;
		ctx.fill();
	}

	const draw = useCallback((ctx, frameCount, c) => {
		if (typeof dominoes === 'undefined' || typeof dominoes.map !== 'function') {
			return;
		}
		if (blockNextDraw) {
			blockNextDraw = false;
			console.log('blocked redraw');
			return;
		}
		console.log('draw ');
		context = ctx;
		canvas = c;
		setFetchingGroup(true);

		drawGrid(ctx);

		dominoes.map((domino) => {
			return drawDomino(
				ctx,
				{
					column: domino.x,
					line: domino.y,
					col: domino.color.color
				},
				true
			);
		});
		if (fetching !== true) {
			setFetchingGroup(false);
		}

		copyCanvasOnMap();

	}, [dominoes, setup])

	const addDomino = useCallback((column, line, save = true) => {

		if (!color && tools !== TOOLS_KEY.rubber) {
			return;
		}

		drawDomino(
			context,
			{
				column,
				line
			}
		);

		if (save) {
			blockNextDraw = true;
			saveMultipleDomino({
				groupId: group.id,
				groupName: group.name,
				colorId: color.id,
				colorCode: color.color,
				dominos: [
					{
						column,
						line
					}
				]
			});
		}
	}, [drawDomino, saveMultipleDomino, tools, group, color]);

	const checkPaint = (elm, checkRule = false) => {
		if (Html.hasSomeParentTheClass(elm, 'btntool')) {
			return;
		}

		if (mode === MODE_KEY.select) {
			return false;
		}

		if (checkRule && isMovePaint === false) {
			return false;
		}
		if (!color && tools !== TOOLS_KEY.rubber) {
			return false;
		}
		if (tools !== TOOLS_KEY.paint && tools !== TOOLS_KEY.rubber) {
			return false;
		}
		return true;
	}

	const onDown = useCallback((event) => {
		if (!checkPaint(event.target)) {
			return;
		}

		isMovePaint = true;
		dominoPaintWithMove = [];
	}, [addDomino, mode]);

	const onUp = useCallback((event) => {
		if (!checkPaint(event.target)) {
			return;
		}
		isMovePaint = false;
		let dominoXY = [];
		for (const [key, value] of Object.entries(dominoPaintWithMove)) {
			dominoXY.push(value);
		}

		if (dominoXY.length > 0) {
			blockNextDraw = true;
			saveMultipleDomino({
				groupId: group.id,
				groupName: group.name,
				colorId: color.id,
				colorCode: color.color,
				dominos: dominoXY
			});
			copyCanvasOnMap();
		} else {
			let clientX = event.offsetX * currentZoom;
			let clientY = event.offsetY * currentZoom;

			let {column, line} = getLineAndColumn(clientX, clientY, true, currentZoom);
			if (column !== false && line !== false) {
				addDomino(column, line);
			}
		}

	}, [tools, group, color, mode]);

	const onMove = useCallback((event) => {
		if (!checkPaint(event.target, true)) {
			return;
		}
		window.clearTimeout(timer);
		let clientX = event.offsetX * currentZoom;
		let clientY = event.offsetY * currentZoom;
		let {column, line} = getLineAndColumn(clientX, clientY, true, currentZoom);
		if (column === false || line === false) {
			return;
		}
		dominoPaintWithMove[column + '-' + line] = {
			column,
			line
		}
		addDomino(column, line, false);
	}, [addDomino, mode]);

	useEffect(() => {
		if (mode !== MODE_KEY.select) {
			return;
		}
		if (tools === false) {
			return;
		}
		if (
			selection.x1 !== false &&
			selection.x2 !== false &&
			selection.y1 !== false &&
			selection.y2 !== false
		) {
			let xMin = (selection.x1 > selection.x2 ? selection.x2 : selection.x1);
			let xMax = (selection.x1 > selection.x2 ? selection.x1 : selection.x2);
			let yMin = (selection.y1 > selection.y2 ? selection.y2 : selection.y1);
			let yMax = (selection.y1 > selection.y2 ? selection.y1 : selection.y2);
			let hasChange = false;

			if (tools === TOOLS_KEY.rubber) {
				hasChange = true;

				context.clearRect(xMin, yMin, xMax - xMin, yMax - yMin);
				drawGrid(context, false);
			}

			xMin *= currentZoom;
			xMax *= currentZoom;
			yMin *= currentZoom;
			yMax *= currentZoom;

			let {
				column: columnMin,
				line: lineMin
			} = getLineAndColumn(xMin, yMin, true, currentZoom);

			let {
				column: columnMax,
				line: lineMax
			} = getLineAndColumn(xMax, yMax, true, currentZoom);


			if (tools === TOOLS_KEY.paint && color) {
				hasChange = true;
				for (let x = columnMin; x <= columnMax; x++) {
					for (let y = lineMin; y <= lineMax; y++) {
						addDomino(x, y, false);
					}
				}
			}

			if (hasChange) {
				changeTools(false);
				setCurrent(false);
				saveSelection({
					groupId: group.id,
					groupName: group.name,
					colorId: color.id,
					colorCode: color.color,
					columnMin,
					columnMax,
					lineMin,
					lineMax,
				});
				copyCanvasOnMap();
			}

		}
	}, [tools, selection, color, setup]);

	useEffect(() => {
		if (moveGroup === false) {
			let xMin = (selection.x1 > selection.x2 ? selection.x2 : selection.x1) * currentZoom;
			let xMax = (selection.x1 > selection.x2 ? selection.x1 : selection.x2) * currentZoom;
			let yMin = (selection.y1 > selection.y2 ? selection.y2 : selection.y1) * currentZoom;
			let yMax = (selection.y1 > selection.y2 ? selection.y1 : selection.y2) * currentZoom;

			let {
				column: columnMin,
				line: lineMin
			} = getLineAndColumn(xMin, yMin, true, currentZoom);


			let {
				column: columnMax,
				line: lineMax
			} = getLineAndColumn(xMax, yMax, true, currentZoom);


			disabledMoveGroup(wrapper, currentZoom, {
				columnMin,
				columnMax,
				lineMin,
				lineMax
			}, () => {
				props.setFetchingGroup(true);
			});
		}
		if (moveGroup !== false) {
			setMoveGroup(canvas, context, wrapper, currentZoom, selection);
			setFetchingGroup(false);
		}
	}, [moveGroup, disabledMoveGroup, setMoveGroup, selection]);


	useEffect(() => {
		if (moveGroup !== false) {
			updateZoom(wrapper, currentZoom);
		}
	}, [currentZoom]);

	return (
		<Canvas
			style={{
				transform: 'scale(' + currentZoom + ')'
			}}
			draw={draw}
			width={planner.width}
			height={planner.height}
			option={{
				onDown,
				onUp,
				onMove
			}}
		/>
	);
}

export default Workspace;
